import { useEffect, useMemo, useState } from "react";
import { Button, Modal, Select, Stack, Text } from "@mantine/core";
import axiosInstance from "../axiosInstance";
import { INT_MAX } from "../utils/util";
import { labels } from "../constants/uiActions";
import {
  type WorkEnvironment,
  formatWorkEnvironmentLabel,
} from "../contexts/WorkEnvironmentContext";

type TerminalModelRow = {
  terminalModelId: number;
  internalModelName: string;
  externalModelName: string;
  vanType: number;
  dikYn?: string;
  serialCableCount?: number;
  useYn?: string;
};

type Props = {
  opened: boolean;
  onClose: () => void;
  current: WorkEnvironment | null;
  onConfirm: (env: WorkEnvironment) => void;
};

const formatModelOptionLabel = (
  model: TerminalModelRow,
  vanLabelMap: Record<string, string>,
) => {
  const vanName = vanLabelMap[String(model.vanType)] ?? String(model.vanType);
  return `${model.internalModelName} / ${model.externalModelName} · ${vanName}`;
};

const WorkEnvironmentModal = ({
  opened,
  onClose,
  current,
  onConfirm,
}: Props) => {
  const [vanLabelMap, setVanLabelMap] = useState<Record<string, string>>({});
  const [models, setModels] = useState<TerminalModelRow[]>([]);
  const [terminalModelId, setTerminalModelId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!opened) return;
    setTerminalModelId(current ? String(current.terminalModelId) : null);
  }, [opened, current]);

  useEffect(() => {
    if (!opened) return;
    setLoading(true);
    Promise.all([
      axiosInstance.get("/api/v1/common/van"),
      axiosInstance.get("/api/v1/terminal/model/list", {
        params: { currentPageNo: 1, recordsPerPage: INT_MAX, useYn: "Y" },
      }),
    ])
      .then(([vanRes, modelRes]) => {
        const vanMap: Record<string, string> = {};
        for (const item of vanRes.data?.data ?? []) {
          vanMap[String(item.code)] = item.name;
        }
        setVanLabelMap(vanMap);

        const list = (modelRes.data?.data?.list ?? []).filter(
          (item: TerminalModelRow) =>
            String(item.useYn || "").toUpperCase() === "Y",
        );
        setModels(list);
      })
      .catch(() => {
        setVanLabelMap({});
        setModels([]);
      })
      .finally(() => setLoading(false));
  }, [opened]);

  const modelOptions = useMemo(
    () =>
      models.map((m) => ({
        value: String(m.terminalModelId),
        label: formatModelOptionLabel(m, vanLabelMap),
      })),
    [models, vanLabelMap],
  );

  const selectedModel = useMemo(
    () =>
      models.find((m) => String(m.terminalModelId) === terminalModelId) ?? null,
    [models, terminalModelId],
  );

  const handleConfirm = () => {
    if (!selectedModel) return;
    const vanName =
      vanLabelMap[String(selectedModel.vanType)] ??
      String(selectedModel.vanType);
    const dikYn =
      String(selectedModel.dikYn || "N").toUpperCase() === "Y" ? "Y" : "N";
    const serialCableCount = Number(selectedModel.serialCableCount) || 1;
    onConfirm({
      terminalModelId: selectedModel.terminalModelId,
      internalModelName: selectedModel.internalModelName,
      externalModelName: selectedModel.externalModelName,
      vanType: selectedModel.vanType,
      vanName,
      dikYn,
      serialCableCount,
    });
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={labels.workEnvironment}
      centered
      radius="md"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          단말기 모델을 선택하세요. 키 주입 화면이 선택한 모델에 맞게
          동작합니다.
        </Text>
        <Select
          label="단말기 모델"
          placeholder="내부모델 / 외부모델 · VAN"
          data={modelOptions}
          value={terminalModelId}
          onChange={setTerminalModelId}
          disabled={loading}
          searchable
          nothingFoundMessage="항목 없음"
        />
        {selectedModel && (
          <Text size="sm">
            DIK 라인:{" "}
            {String(selectedModel.dikYn || "N").toUpperCase() === "Y"
              ? `예 (${(Number(selectedModel.serialCableCount) || 1) === 1 ? "1포트" : "2포트"} · S/N → DIK → MAC)`
              : "아니오 (MAC → S/N)"}
          </Text>
        )}
        {current && (
          <Text size="xs" c="dimmed">
            현재: {formatWorkEnvironmentLabel(current)}
          </Text>
        )}
        <Button onClick={handleConfirm} disabled={!selectedModel}>
          {labels.confirm}
        </Button>
      </Stack>
    </Modal>
  );
};

export default WorkEnvironmentModal;
